import { Children, isValidElement, useEffect, type ComponentPropsWithoutRef, type ReactElement, type ReactNode } from "react";
import { defaultUrlTransform, type Components } from "react-markdown";
import { sizeClasses } from "./mediaSize";
import { parseMediaBlock } from "./mediaBlock";
import MessageMedia from "../components/MessageMedia";
import { useRichMedia } from "../contexts/RichMediaContext";

const linkClassName = "underline text-lastbot-link hover:text-lastbot-link-hover";

const MEDIA_LANG = "language-lastbot-media";

const MarkdownImage = ({ src, alt, title }: ComponentPropsWithoutRef<"img">): ReactElement => {
  const { reportMedia } = useRichMedia();
  const visibleSrc = typeof src === "string" ? src.trim() : "";

  // react-markdown's default URL transform blanks unsafe protocols before this
  // component is created. An empty src renders no visible media, so it must not
  // affect popup sizing.
  useEffect(() => {
    if (visibleSrc) reportMedia();
  }, [reportMedia, visibleSrc]);

  const classes = sizeClasses(title);
  const className = classes.length ? classes.join(" ") : undefined;
  const tooltip = classes.length ? undefined : title;
  return <img src={src} alt={alt} title={tooltip} className={className} />;
};

const MarkdownLink = ({
  href,
  children,
  ...props
}: ComponentPropsWithoutRef<"a">): ReactElement | null => {
  if ("data-footnote-backref" in props) return null;
  const isMailOrTel = /^(?:mailto|tel):/i.test(href ?? "");
  if (isMailOrTel) {
    return <a href={href} className={linkClassName}>{children}</a>;
  }
  return <a data-url={href} href="#" className={linkClassName}>{children}</a>;
};

/**
 * True when this paragraph is a caption figure: it leads with an image (bare, or
 * wrapped in a link as PRODUCT FORMAT emits) and carries further content after
 * it — the caption the prompt puts on the very next line.
 *
 * The caption shares the image's paragraph via a soft line break, so it is a
 * sibling *text node*, which no CSS selector can match. Detecting the shape here
 * lets the stylesheet pull the caption up against the image it describes.
 */
const isCaptionFigure = (children: ReactNode): boolean => {
  if (!Array.isArray(children) || children.length < 2) return false;

  const [first, ...rest] = children;
  if (!isValidElement(first)) return false;

  const leadsWithImage =
    first.type === MarkdownImage ||
    first.type === "img" ||
    ((first.type === MarkdownLink || first.type === "a") &&
      Children.toArray((first.props as { children?: ReactNode }).children).some(
        (child) => isValidElement(child) && (child.type === MarkdownImage || child.type === "img"),
      ));
  if (!leadsWithImage) return false;

  // A trailing soft break alone is not a caption; require real content.
  return rest.some((child) => (typeof child === "string" ? child.trim().length > 0 : child != null && child !== false));
};

/** Extract the inner <code> element react-markdown nests inside a <pre>. */
const findCodeChild = (children: ReactNode): ReactElement | null => {
  if (isValidElement(children)) return children;
  if (Array.isArray(children)) {
    const found = children.find(isValidElement);
    return found ?? null;
  }
  return null;
};

// react-markdown blanks every href whose protocol is not in its allowlist (https, mailto, ...).
// tel: is safe and needed for tappable phone links; everything else keeps the default sanitizing.
export const urlTransform = (url: string): string =>
  /^tel:/i.test(url) ? url : defaultUrlTransform(url);

export const markdownComponents: Components = {
  a: MarkdownLink,
  img: MarkdownImage,
  p: ({ children }): ReactElement => (
    <p className={isCaptionFigure(children) ? "lastbot-caption-figure" : undefined}>{children}</p>
  ),
  pre: ({ children }): ReactElement | null => {
    // Intercept fenced ```lastbot-media``` blocks and render sized media.
    // Replacing the whole <pre> avoids invalid <pre><div> nesting. Every other
    // fenced block renders as a normal <pre>.
    const code = findCodeChild(children);
    const props = (code?.props ?? {}) as {
      className?: string;
      children?: ReactNode;
    };
    if (typeof props.className === "string" && props.className.includes(MEDIA_LANG)) {
      const descriptor = parseMediaBlock(String(props.children ?? ""));
      // Valid descriptor → custom media render; malformed block → fall back to
      // the normal code rendering so the raw content isn't silently dropped.
      return descriptor ? (
        <MessageMedia descriptor={descriptor} />
      ) : (
        <pre>{children}</pre>
      );
    }
    return <pre>{children}</pre>;
  },
};
